import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  CoverageStatus,
  EmployerTypeAnswer,
  PaidByPartnerHospital,
  ResultStage,
  StudentLoanCalculationParameters,
} from 'api/StudentLoanApi';
import FormNavigation from 'components/FormNavigation';
import RadioButtonList from 'components/Common/RadioButtonList/RadioButtonList';
import Button from 'components/Button';
import StudentLoanLoaderWrapper from 'components/StudentLoanForgiveness/LoaderWrapper';
import { InfoNote } from 'components/StudentLoanForgiveness/Common/ResultBlocks';
import { formatHospitalName, getHospitalOwnershipCopy } from 'components/StudentLoanForgiveness/studentLoanCopy';
import useSaveStudentLoanAnswers, { reportStudentLoanSaveFailure } from 'hooks/useSaveStudentLoanAnswers';
import { useNavigate, useNavigationType } from 'hooks/useNavigate';
import { RoutePath } from 'enums/Routes';
import { FlowComponentType } from 'routes/types';
import { getStudentLoanStart } from 'routes/StudentLoanForgivenessRoutes';
import { getStudentLoanData } from 'selectors/getStudentLoanData';
import { ConversionType, trackConversion } from 'utils/analytics';

import QuestionsWrapper from './Wrapper/QuestionsWrapper';

import styles from './Questions.module.scss';

const NONE_OF_THESE = 'none';

const paidOptions = [
  { id: PaidByPartnerHospital.Yes, label: 'Yes' },
  { id: PaidByPartnerHospital.No, label: 'No, another employer or a staffing agency pays me' },
  { id: PaidByPartnerHospital.NotSure, label: "I'm not sure" },
];

const employerTypeOptions = [
  { id: EmployerTypeAnswer.Nonprofit, label: 'A nonprofit, 501(c)(3)' },
  { id: EmployerTypeAnswer.Government, label: 'A government employer' },
  { id: EmployerTypeAnswer.ForProfit, label: 'A for-profit company or staffing agency' },
  { id: EmployerTypeAnswer.NotSure, label: "I'm not sure" },
];

const getInitialChoice = (
  { paidByPartnerHospital, selectedHospitalFacilityId }: StudentLoanCalculationParameters,
  isAmbiguous: boolean,
): string | null => {
  if (!isAmbiguous) return null;
  if (selectedHospitalFacilityId) return selectedHospitalFacilityId;
  return paidByPartnerHospital === PaidByPartnerHospital.No ? NONE_OF_THESE : null;
};

const Employer = ({ navigationInfo, handleNext }: FlowComponentType) => {
  const studentLoanData = useSelector(getStudentLoanData);
  const { answers, hospital, result, fetched } = studentLoanData;
  const { saveAnswers, isSaving } = useSaveStudentLoanAnswers();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const resumedPastEmployer = useRef(false);
  const trackedOfferAvailable = useRef(false);

  const isMatched = hospital?.status === CoverageStatus.Matched && Boolean(hospital.facility);
  const isAmbiguous = hospital?.status === CoverageStatus.Ambiguous && hospital.choices.length > 0;
  const hospitalName = hospital?.facility ? formatHospitalName(hospital.facility.name) : '';

  const [paid, setPaid] = useState<PaidByPartnerHospital | null>(
    answers.paidByPartnerHospital ?? (isMatched ? PaidByPartnerHospital.Yes : null),
  );
  const [choice, setChoice] = useState<string | null>(getInitialChoice(answers, isAmbiguous));
  const [employerType, setEmployerType] = useState<EmployerTypeAnswer | null>(answers.employerType ?? null);

  // Preselect the matched hospital, and restore anything the borrower already saved.
  useEffect(() => {
    setPaid(answers.paidByPartnerHospital ?? (isMatched ? PaidByPartnerHospital.Yes : null));
    if (answers.employerType !== undefined) {
      setEmployerType(answers.employerType);
    }
    setChoice(getInitialChoice(answers, isAmbiguous));
  }, [answers.paidByPartnerHospital, answers.employerType, answers.selectedHospitalFacilityId, isAmbiguous, isMatched]);

  // Count the offer once per visit to this screen. Back and resume land here without a new offer.
  useEffect(() => {
    if (trackedOfferAvailable.current || !fetched || navigationType === 'POP') {
      return;
    }
    if (getStudentLoanStart(studentLoanData) !== RoutePath.StudentLoanEmployer) {
      return;
    }
    trackedOfferAvailable.current = true;
    trackConversion(ConversionType.StudentLoanOfferAvailable);
  }, [fetched, navigationType, studentLoanData]);

  // Resume / dashboard entry: skip re-asking when answers + stage already point elsewhere.
  // Do not redirect on browser back so screens stay editable.
  useEffect(() => {
    if (!fetched || navigationType === 'POP' || resumedPastEmployer.current) {
      return;
    }
    const start = getStudentLoanStart(studentLoanData);
    if (start !== RoutePath.StudentLoanEmployer) {
      resumedPastEmployer.current = true;
      navigate(start);
    }
  }, [fetched, navigationType, studentLoanData, navigate]);

  // Entry screen: borrowers with nothing to estimate (or a blocking default) skip the questions.
  useEffect(() => {
    if (fetched && (result?.stage === ResultStage.NoFederalLoans || result?.stage === ResultStage.Blocked)) {
      handleNext(result.stage);
    }
  }, [fetched, result?.stage]);

  let needsEmployerType = true;
  let isFacilityAnswered = true;
  if (isMatched) {
    needsEmployerType = paid === PaidByPartnerHospital.No;
    isFacilityAnswered = paid !== null;
  } else if (isAmbiguous) {
    needsEmployerType = choice === NONE_OF_THESE;
    isFacilityAnswered = choice !== null;
  }
  const canContinue = isFacilityAnswered && (!needsEmployerType || employerType !== null);

  const buildAnswers = (): StudentLoanCalculationParameters => {
    const employerTypeAnswer = needsEmployerType ? employerType : null;
    if (isMatched) {
      return { paidByPartnerHospital: paid, employerType: employerTypeAnswer };
    }
    if (isAmbiguous && choice !== NONE_OF_THESE) {
      return {
        paidByPartnerHospital: PaidByPartnerHospital.Yes,
        selectedHospitalFacilityId: choice,
        employerType: null,
      };
    }
    return {
      paidByPartnerHospital: isAmbiguous ? PaidByPartnerHospital.No : null,
      selectedHospitalFacilityId: null,
      employerType: employerTypeAnswer,
    };
  };

  const handleClick = async () => {
    const savedAnswers = buildAnswers();
    analytics.track('Student Loan Employer Selected', { ...savedAnswers });
    try {
      handleNext(await saveAnswers(savedAnswers));
    } catch {
      reportStudentLoanSaveFailure();
    }
  };

  return (
    <StudentLoanLoaderWrapper>
      <>
        <FormNavigation {...navigationInfo} />
        <QuestionsWrapper title="Let's estimate your loan forgiveness" showBalance>
          <>
            {isMatched && (
              <RadioButtonList
                label={`Is your paycheck from ${hospitalName}?`}
                selected={paid}
                setSelected={setPaid}
                options={paidOptions}
              />
            )}

            {isMatched && paid === PaidByPartnerHospital.Yes && hospital && (
              <InfoNote {...getHospitalOwnershipCopy(hospitalName, hospital.ownership)} />
            )}

            {isAmbiguous && hospital && (
              <RadioButtonList
                label="Which hospital pays you?"
                selected={choice}
                setSelected={setChoice}
                options={[
                  ...hospital.choices.map(({ facilityId, name, city, state }) => ({
                    id: facilityId,
                    label: `${formatHospitalName(name)}, ${formatHospitalName(city)} ${state}`,
                  })),
                  { id: NONE_OF_THESE, label: 'None of these' },
                ]}
              />
            )}

            {needsEmployerType && (
              <RadioButtonList
                label="What kind of employer pays you?"
                selected={employerType}
                setSelected={setEmployerType}
                options={employerTypeOptions}
              />
            )}

            <p className={styles.hint}>Most hospitals and universities are nonprofits.</p>

            <Button disabled={!canContinue} isLoading={isSaving} onClick={handleClick}>
              Next
            </Button>
          </>
        </QuestionsWrapper>
      </>
    </StudentLoanLoaderWrapper>
  );
};

export default Employer;
